/**
 * Rock BTS — frontend computation mirrors for live preview.
 *
 * SYNC-POINTER: these are exact mirrors of the backend pure functions in
 * backend/src/calculation/rockBtsCalculations.ts and
 * backend/src/calculation/rockBtsFailureDescription.ts — keep both in step.
 * The backend recomputes authoritatively on every save.
 */
import type { RockBTSSpecimen } from '../../../types/rockBts';
import { BTS_MAX_STRESS_RATE_MPAS, BTS_MIN_STRESS_RATE_MPAS } from './rockBtsFields';

const BTS_DEVIATION_THRESHOLD = 0.1;

export interface BtsSpecimenPreview {
  avgDiameterMm: number | null;
  avgThicknessMm: number | null;
  volumeCm3: number | null;
  bulkDensityGCm3: number | null;
  loadingRateMPas: number | null;
  minRateKNs: number | null;
  maxRateKNs: number | null;
  minRateMPas: number;
  maxRateMPas: number;
  rateOutsideAstmRange: boolean | null;
  tensileStrengthMPa: number | null;
  deviationXAvgMm: number | null;
  deviationRatio: number | null;
  suggestedClassification: 'Central' | 'Non-Central' | null;
  failureDescription: string | null;
}

export function average(values: (number | null | undefined)[]): number | null {
  const nums = values.filter((v): v is number => v !== null && v !== undefined && Number.isFinite(v));
  if (nums.length === 0) return null;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

/** Live preview of every derived value for one specimen (mirrors the backend recompute). */
export function computeBtsSpecimenPreview(s: RockBTSSpecimen): BtsSpecimenPreview {
  const avgDiameterMm = average([s.diameter1, s.diameter2, s.diameter3]);
  const avgThicknessMm = average([s.thickness1, s.thickness2, s.thickness3]);
  const volumeCm3 =
    avgDiameterMm !== null && avgThicknessMm !== null
      ? ((avgDiameterMm / 2) ** 2 * Math.PI * avgThicknessMm) / 1000
      : null;
  const bulkDensityGCm3 =
    volumeCm3 !== null && volumeCm3 > 0 && s.massG !== null && s.massG !== undefined
      ? s.massG / volumeCm3
      : null;

  let loadingRateMPas: number | null = null;
  let minRateKNs: number | null = null;
  let maxRateKNs: number | null = null;
  let rateOutsideAstmRange: boolean | null = null;
  if (avgDiameterMm !== null && avgThicknessMm !== null && avgDiameterMm > 0 && avgThicknessMm > 0) {
    const dt = Math.PI * avgDiameterMm * avgThicknessMm;
    minRateKNs = (BTS_MIN_STRESS_RATE_MPAS * dt) / 2 / 1000;
    maxRateKNs = (BTS_MAX_STRESS_RATE_MPAS * dt) / 2 / 1000;
    if (s.loadingRateKNs !== null && s.loadingRateKNs !== undefined) {
      loadingRateMPas = (2 * s.loadingRateKNs * 1000) / dt;
      rateOutsideAstmRange = s.loadingRateKNs < minRateKNs || s.loadingRateKNs > maxRateKNs;
    }
  }

  const tensileStrengthMPa =
    s.maxLoadKN !== null && s.maxLoadKN !== undefined &&
    avgDiameterMm !== null && avgThicknessMm !== null && avgDiameterMm > 0 && avgThicknessMm > 0
      ? (2 * s.maxLoadKN * 1000) / (Math.PI * avgDiameterMm * avgThicknessMm)
      : null;

  const deviationXAvgMm = average([s.deviationXFrontMm, s.deviationXBackMm]);
  let deviationRatio: number | null = null;
  let suggestedClassification: 'Central' | 'Non-Central' | null = null;
  if (deviationXAvgMm !== null && avgDiameterMm !== null && avgDiameterMm > 0) {
    deviationRatio = (deviationXAvgMm - avgDiameterMm / 2) / avgDiameterMm;
    suggestedClassification = deviationRatio > BTS_DEVIATION_THRESHOLD ? 'Non-Central' : 'Central';
  }

  return {
    avgDiameterMm,
    avgThicknessMm,
    volumeCm3,
    bulkDensityGCm3,
    loadingRateMPas,
    minRateKNs,
    maxRateKNs,
    minRateMPas: BTS_MIN_STRESS_RATE_MPAS,
    maxRateMPas: BTS_MAX_STRESS_RATE_MPAS,
    rateOutsideAstmRange,
    tensileStrengthMPa,
    deviationXAvgMm,
    deviationRatio,
    suggestedClassification,
    failureDescription: buildBtsFailureDescriptionPreview(s),
  };
}

// ── Failure-sentence builder (mirror of rockBtsFailureDescription.ts) ────────

interface FailureRow {
  fractureMode: string | null;
  planeType: string | null;
  angleADeg: number | null;
  angleBDeg: number | null;
}

const prep = (plane: string): string => (plane === 'rock matrix' ? 'through ' : 'along ');
const article = (plane: string): string => {
  if (plane === 'vein' || plane === 'microdefect' || plane === 'foliation') return 'a ';
  if (plane === 'intrusion') return 'an ';
  return '';
};
const plural = (plane: string): string =>
  plane === 'vein' || plane === 'microdefect' || plane === 'intrusion' ? 's' : '';

const hasAngles = (r: FailureRow) => r.angleADeg !== null && r.angleBDeg !== null;
const hasModeAndPlane = (r: FailureRow) => !!r.fractureMode && !!r.planeType;

const failureClause = (r: FailureRow, pluralize: boolean): string => {
  const mode = r.fractureMode as string;
  const plane = r.planeType as string;
  if (pluralize) return `${mode} failures ${prep(plane)}${plane}${plural(plane)}`;
  return `${mode} failure ${prep(plane)}${article(plane)}${plane}`;
};

const anglesSuffix = (r: FailureRow): string =>
  ` at ${r.angleADeg}° to the load axis and ${r.angleBDeg}° to the core axis`;

function buildSentence(failureType: string | null, primary: FailureRow, secondary: FailureRow): string | null {
  if (!failureType) return null;
  switch (failureType) {
    case 'Layer Activation':
      if (!hasModeAndPlane(primary) || !hasAngles(primary)) return null;
      return `Layer activation ${failureClause(primary, false)}${anglesSuffix(primary)}.`;
    case 'Non-Central':
      if (!hasModeAndPlane(primary)) return null;
      return `Non-central ${failureClause(primary, false)}.`;
    case 'Central':
      if (!hasModeAndPlane(primary) || !hasAngles(primary)) return null;
      return `Central ${failureClause(primary, false)}${anglesSuffix(primary)}.`;
    case 'Multiple':
      if (!hasModeAndPlane(primary)) return null;
      return `Multiple ${failureClause(primary, true)}.`;
    case 'Invalid':
      if (!hasModeAndPlane(primary)) return null;
      return `Invalid ${failureClause(primary, false)}.`;
    case 'Non-Central & Layer Activation':
    case 'Central & Layer Activation':
    case 'Multiple & Layer Activation': {
      const base = buildSentence(failureType.replace(' & Layer Activation', ''), primary, secondary);
      if (base === null || !hasModeAndPlane(secondary) || !hasAngles(secondary)) return null;
      return `${base} Layer activation ${failureClause(secondary, false)}${anglesSuffix(secondary)}.`;
    }
    default:
      return null;
  }
}

export function buildBtsFailureDescriptionPreview(s: RockBTSSpecimen): string | null {
  return buildSentence(
    s.failureType,
    {
      fractureMode: s.primaryFractureMode,
      planeType: s.primaryPlaneType,
      angleADeg: s.primaryAngleADeg,
      angleBDeg: s.primaryAngleBDeg,
    },
    {
      fractureMode: s.secondaryFractureMode,
      planeType: s.secondaryPlaneType,
      angleADeg: s.secondaryAngleADeg,
      angleBDeg: s.secondaryAngleBDeg,
    },
  );
}

/** Override beats auto (workbook B84/B100). */
export function effectiveBtsDescription(s: RockBTSSpecimen): string | null {
  const override = s.failureDescriptionOverride?.trim();
  if (override) return override;
  return buildBtsFailureDescriptionPreview(s);
}
